/**
 * Shipments tab for the Order Detail page.
 * Renders a list of shipment cards showing contact, address, shipping method,
 * tracking info, delivery date, and an expandable items accordion per shipment.
 */
import React from "react";
import { Box, createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { useOutletContext } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { Body } from "../../../components/Typography";
import { OrderViewV2OutletContext } from "../OrderViewV2";
import { ShipmentCard } from "./shipments/ShipmentCard";

const useStyles = createStyles({
  container: {
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  emptyState: {
    padding: tokens.spacing[4],
  },
});

export const OrderShipmentsTab: React.FC = () => {
  const { classes } = useStyles();
  const { order } = useOutletContext<OrderViewV2OutletContext>();
  const user = useAuthUser();

  if (!order) return null;

  const shipments = order.shipments ?? [];
  const currency = order.currency ?? GraphQL.SupportedCurrency.Usd;

  if (shipments.length === 0) {
    return (
      <Box data-testid="order-shipments-loaded" className={classes.emptyState}>
        <Body>No shipments found for this order.</Body>
      </Box>
    );
  }

  return (
    <Box data-testid="order-shipments-loaded" className={classes.container}>
      {shipments.map((shipment) => (
        <ShipmentCard key={shipment.id} shipment={shipment} currency={currency} isSuperAdmin={user.is.SUPER_ADMIN} />
      ))}
    </Box>
  );
};
